import clsx from 'clsx'
import Group from 'components/utils/Group'
import type { ComponentProps, ReactNode, Ref } from 'react'
import { createContext, useContext } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'

export type UspTileSize = 'small' | 'large'

type UspsLayoutSize = UspTileSize | 'responsive'

export const UspsLayoutSizeContext = createContext<UspsLayoutSize | undefined>(undefined)

const DEFAULT_ACCENT_COLOUR: App.Design.ContentColour = 'highlight-primary-dark'

const Root = styled(Group)`
  border-radius: ${props => props.theme.borderRadius.L};

  &.size-responsive {
    ${mediaQueryUp.desktop} {
      .align-start {
        text-align: center;
      }
    }
  }
`

const IconSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.palette.highlight.primary.darkForeground};

  &.size-small {
    ${setSvgIconSize('S')}
  }

  &.size-large {
    ${setSvgIconSize('L')}
  }

  &.size-responsive {
    ${setSvgIconSize('S')}

    ${mediaQueryUp.desktop} {
      ${setSvgIconSize('L')}
    }
  }
`

const TileHeading = styled(BodyText)`
  text-wrap: balance;
`

interface Props extends Utils.PropsWithDataAttributes {
  ref?: Ref<HTMLDivElement>
  /**
   * `large` stacks the icon above centred copy.
   * `small` places the icon beside start-aligned copy.
   * When omitted inside `UspsBase`/`Usps`, inherits the section size.
   * When the section also omits size, CSS switches at the desktop breakpoint.
   *
   * @default 'large' when rendered outside a USPs section
   */
  size?: UspTileSize
  /** Leading icon. Size is applied by the tile (`L` / 32px for large, `S` / 20px for small). */
  icon: ReactNode
  heading?: ReactNode
  /**
   * Optional heading colour override.
   * Defaults to Highlight / Primary / Dark Foreground (`highlight-primary-dark`).
   */
  headingColour?: App.Design.ContentColour
  /**
   * Element for the tile title. Defaults to BodyText’s root (`div`) so the title
   * stays a label, matching Global Kit Body M. Pass `h4` (or similar) when the
   * page outline should include each USP.
   */
  headingAs?: ComponentProps<typeof BodyText>['as']
  children?: ReactNode
  /** Typically a `TextLink` (medium, bold, underline). Colour is owned by the link. */
  action?: ReactNode
  className?: string
}

function UspTile({
  ref,
  size: sizeProp,
  icon,
  heading,
  headingColour,
  headingAs,
  children,
  action,
  className,
  ...rest
}: Props) {
  const sizeFromSection = useContext(UspsLayoutSizeContext)
  const isResponsive = sizeProp == null && sizeFromSection === 'responsive'
  const size: UspTileSize = isResponsive
    ? 'small'
    : (sizeProp ?? (sizeFromSection === 'large' || sizeFromSection === 'small' ? sizeFromSection : 'large'))
  const isLarge = !isResponsive && size === 'large'
  const align = isLarge ? 'center' : 'start'
  const sizeClassName = isResponsive ? 'size-responsive' : `size-${size}`
  const hasCopy = heading != null || children != null
  const hasContent = hasCopy || action != null

  return <Root
    {...rest}
    ref={ref}
    className={clsx(className, sizeClassName)}
    direction={isLarge ? 'vertical' : 'horizontal'}
    desktopDirection={isResponsive ? 'vertical' : undefined}
    gap={12}
    verticalAlign="start"
    horizontalAlign={isLarge ? 'center' : 'start'}
    desktopHorizontalAlign={isResponsive ? 'center' : undefined}
    fullWidth
  >
    <IconSlot className={sizeClassName}>
      {icon}
    </IconSlot>
    {hasContent && <Group
      direction="vertical"
      gap={8}
      horizontalAlign={isLarge ? 'center' : 'start'}
      desktopHorizontalAlign={isResponsive ? 'center' : undefined}
      fullWidth
      grow
    >
      {hasCopy && <Group
        direction="vertical"
        gap={2}
        horizontalAlign="stretch"
        fullWidth
      >
        {heading != null && <TileHeading
          as={headingAs}
          variant="medium"
          weight="bold"
          colour={headingColour ?? DEFAULT_ACCENT_COLOUR}
          align={align}
          wrap="word-wrap"
        >
          {heading}
        </TileHeading>}
        {children != null && <BodyText
          variant="medium"
          weight="normal"
          colour="light-foreground-alt"
          align={align}
          wrap="pretty"
        >
          {children}
        </BodyText>}
      </Group>}
      {action != null && action}
    </Group>}
  </Root>
}

export default UspTile
